define((require) => {
    "use strict";

    var $                   = require("jquery"),
        CommentCollection   = require("ui/comments/CommentCollection"),
        {DateUtil}          = require("shared/util/DateUtil"),
        EditorEvents        = require("shared/services/EditorEvents"),
        EditBehavior        = require("ui/behaviors/EditBehavior"),
        ExperienceViewer    = require("shared/ui/viewer/ExperienceViewer"),
        ExperienceCncViewer = require("ui/viewer/cnc/ExperienceCncViewer"),
        ExperienceViewModel = require("shared/ui/viewer/ExperienceViewModel"),
        ImprovedView        = require("editor/zbone/ImprovedView"),
        LoggedInUserState   = require("shared/ui/user/LoggedInUserState"),
        Preconditions       = require("shared/util/Preconditions"),
        Promise             = require("shared/util/Promise"),
        PublishableState    = require("util/PublishableState"),
        QuickViewConstants  = require("ui/experienceOverview/QuickViewConstants"),
        Size                = require("shared/util/Size"),
        SpinnerView         = require("shared/ui/common/SpinnerView"),
        template            = require("ui/experienceOverview/QuickViewExperienceDetailsView.tpl"),
        Tooltip             = require("ui/common/Tooltip"),
        TweenMax            = require("TweenMax"),
        UrlUtil             = require("shared/util/UrlUtil"),
        URLBox              = require("ui/common/URLBox"),
        UserCollection      = require("ui/user/UserCollection");

    var EXPERIENCE_PREVIEW_WIDTH = 498,
        EXPERIENCE_PREVIEW_HEIGHT = 250;

    /**
     * Sub view used by the <tt>QuickView</tt> for displaying experience details, statistics etc.
     *
     * @cfg {ExperienceModel} model The experience model.
     *
     * @class QuickViewExperienceDetailsView
     */
    return ImprovedView.extend({
        _experienceViewer: null,

        template: template,

        templateHelpers: function() {
            return {
                hasValidURL:        this._hasValidURL(),
                humanDate:          DateUtil.formatMMDDYYY.bind(DateUtil),
                editedAt:           this.model.getEditedAt(),
                spectatorMode:      this.options.spectatorMode
            };
        },

        bindings: {
            ".edit-name": {
                // We need to use updateMethod "html" and escape true, because we are using a tag contenteditable
                observe: "name",
                updateMethod: "html",
                escape: true,
                // The dashboard items will be refreshed only when this field is blurred
                events: ["blur"]
            }
        },

        behaviors: {
            editBehavior: {
                behaviorClass: EditBehavior,
                container: ".editable-container",
                editFields: [{
                    input: ".edit-name",
                    model: "name",
                    save: function(val) {
                        this.model.set("name", val);

                        return this.model.saveLater().done(() => {
                            var eventProperties = {
                                experienceId: this.model.get("id"),
                                experienceVersion: this.model.get("version"),
                                experiencePushedVersion: this.model.get("pushedVersion"),
                                triggerOrigin: "EditExperienceNameInputField",
                                projectId: this.options.projectContext && this.options.projectContext.project.get("id"),
                                updatedProperty: "name",
                                quickView: true,
                                updatedValue: val
                            };

                            EditorEvents.trigger(EditorEvents.constants.UPDATE_EXPERIENCE, eventProperties);
                        });
                    }
                }]
            }
        },

        regions: {
            experiencePreview:              ".experience-preview"
        },

        ui: {
            currentExperienceStatus:     ".current-experience-status",
            notPushedChangesWarning:     ".not-pushed-changes-warning",
            pushedCheckMark:             ".pushed-check-mark",
            readableState:               ".readable-state",
            toggleExperienceURL:         ".toggle-experience-url",
            urlContainer:                ".experience-url-container",
            commentsIndicatorContainer:  ".comments-current",
            newCommentsIndicatorWrapper: ".unread-comments-indicator-wrapper",
            newCommentsIndicator:        ".unread-comments-indicator",
            authorName:                  ".author-name",
            editorName:                  ".editor-name",
            experienceIdCopy:            ".experience-id-copy"
        },

        events: {
            "click .edit-experience, .view-experience": "_trackAndClose",
            "click @ui.toggleExperienceURL": "_toggleExperienceUrl",
            "click @ui.experienceIdCopy": "_copyExperienceId"
        },

        initialize: function() {
            // We need to update the UI of push state after the name is changed on the quick view.
            // Simply listen on the change:name won't work
            this.listenTo(this.model, "sync:state", function() {
                // Make sure the view is not closed when the promise is done
                Promise.predicate(this.options.promise, this._viewActive.bind(this)).done(() => {
                    this._updateState();
                });
            });
        },

        /**
         * Performs a fade in animation.
         *
         * @return {Promise} A Promise that resolves when the transition completes.
         */
        fadeIn: function() {
            return new Promise.deferred(((deferred) => {
                TweenMax.to(this.$el, 0.5, {opacity: 1.0, onComplete: deferred.resolve});
            }));
        },

        /**
         * Performs a fade out animation.
         *
         * @return {Promise} A Promise that resolves when the transition completes.
         */
        fadeOut: function() {
            var element = this.el;

            return new Promise.deferred(((deferred) => {
                TweenMax.to(element, 0.5, {opacity: 0.0, onComplete: deferred.resolve});
            }));
        },

        onRender: function() {
            this._initializeURL();
            this._initializeExperienceId();
            this._showComments();
            this._updateState();
            this._updateWorkBy();

            Tooltip.add(this.ui.toggleExperienceURL, {width: 200, placement: "top"});
        },

        onShow: function() {
            var promise = this.options.promise,
                pendingState = Promise.isPending(promise);

            // Display the notorious spinner if data is currently pending
            if (pendingState) {
                this.experiencePreview.show(new SpinnerView());

                // Make sure the view is not closed when the promise is done
                Promise.predicate(promise, this._viewActive.bind(this)).done(() => {
                    this._createPreview();
                });
            } else {
                this._createPreview();
            }
        },

        _viewActive: function() {
            return !this.isClosed;
        },

        _updateState: function() {
            var pushed = this.model.hasAtLeastState(PublishableState.PUSHED),
                nonPushedChanges = this.model.isChangedAfterPush();

            this.ui.currentExperienceStatus.toggleClass("changes", nonPushedChanges);
            this.ui.notPushedChangesWarning.toggle(nonPushedChanges);
            this.ui.pushedCheckMark.toggle(pushed && !nonPushedChanges);

            if (nonPushedChanges) {
                Tooltip.add(this.ui.notPushedChangesWarning, {width: 170, placement: "top"});
            }

            this.ui.readableState.html(this.model.getState().displayName);
        },

        /**
         * @private
         *
         * Creates the <tt>ExperienceViewer</tt> representing the preview.
         */
        _createPreview: function() {
            Preconditions.isModel(this.model);
            const previewSize = new Size(EXPERIENCE_PREVIEW_WIDTH, EXPERIENCE_PREVIEW_HEIGHT);
            const integrations = LoggedInUserState.get("integrations");

            if (integrations.isNativeContentIntegrationsEnabled()) {
                this._experienceViewer = new ExperienceCncViewer({
                    model: new ExperienceViewModel({
                        experience: this.model,
                        fixedSize: previewSize
                    }),
                    startEvent: QuickViewConstants.EventNames.QUICK_VIEW_SHOWN,
                    groupsDisable: true,
                    fitInHeight: true
                });
            } else {
                this._experienceViewer = new ExperienceViewer({
                    // Pass a clone of the experience model as the ExperienceViewer might 'scale' (previewSize) the experience
                    model: new ExperienceViewModel({experience: this.model.clone()}),
                    fixedSize: previewSize,
                    verticalAlign: "top",
                    horizontalAlign: "left"
                });
            }
            this.experiencePreview.show(this._experienceViewer);
        },

        _initializeURL: function() {
            var urlBox = new URLBox({
                urlProvider: this._urlProvider.bind(this),
                helpText: "View your Experience directly with this link.",
                project: this.options.projectContext && this.options.projectContext.project,
                triggerOrigin: "OpenExperienceUrlButton"
            }).render();

            this.ui.urlContainer.html(urlBox.el);
        },

        _urlProvider: function() {
            return UrlUtil.getAbsoluteExperienceUrl(this.model.get("id"));
        },

        _toggleExperienceUrl: function(event) {
            if (this._hasValidURL()) {
                event.preventDefault();

                var open = !this.ui.urlContainer.is(":visible");

                this.ui.urlContainer.slideToggle(open);
                this.ui.toggleExperienceURL.toggleClass("open", open);
            }
        },

        _initializeExperienceId: function() {
            this.ui.experienceIdCopy.popover();
        },

        _copyExperienceId: function(event) {
            window.getSelection().selectAllChildren(event.target);
            document.execCommand("copy");
        },

        _hasValidURL: function() {
            return this.model.hasAtLeastState(PublishableState.PUSHED);
        },

        _showComments: function() {
            if (this.model.get("lastCommentedAt")) {
                var lastVisit = LoggedInUserState.get("lastVisit")[this.model.get("id")];
                lastVisit = (lastVisit && lastVisit.visit) || 0;

                // The view might be closed when the promise is resolved - using Promise.predicate to
                // ensure that the promise is rejected if the view is closed.
                Promise.predicate(CommentCollection.countCommentsSince(this.model.get("id"), lastVisit), this._updatable.bind(this))
                    .done((numberOfComments) => {
                        Preconditions.isNumber(numberOfComments);
                        this.ui.commentsIndicatorContainer.show();

                        if (numberOfComments) {
                            this.ui.newCommentsIndicatorWrapper.show();
                            this.ui.newCommentsIndicator.html(numberOfComments);
                        }
                    });
            }
        },

        /**
         * @private
         *
         * Whether the view can be updated, i.e. the view is not considered closed.
         *
         * @return {Boolean} <tt>true</tt> if the view can be updated, <tt>false</tt> if not.
         */
        _updatable: function() {
            return !this.isClosed;
        },

        /**
         * @private
         *
         * Fetches the user collection to determine who created the experience and who was
         * the last one to edit it - updating the work-by UI accordingly.
         */
        _updateWorkBy: function() {
            // The view might be closed when the promise is resolved - using Promise.predicate to
            // ensure that the promise is rejected if the view is closed.
            Promise.predicate(LoggedInUserState.fetchGlobalCollection(UserCollection), this._updatable.bind(this))
                .done((userCollection) => {
                    var editedByExperienceModel = this.model.get("editedBy"),
                        editedByModel = editedByExperienceModel ? userCollection.findWhere({id: editedByExperienceModel}) : null,
                        userModel = userCollection.findWhere({id: this.model.get("createdBy")}),
                        authorName = userModel ? userModel.get("name") : "Unknown";

                    this.ui.editorName.html(editedByModel ? editedByModel.get("name") : authorName);
                    this.ui.authorName.html(authorName);
                });
        },

        _trackAndClose: function(event) {
            var target = $(event.target),
                experience = this.model,
                eventProperties = {
                    experienceId: experience.get("id"),
                    experienceVersion: experience.get("version"),
                    experiencePushedVersion: experience.get("pushedVersion"),
                    projectId: this.options.projectContext && this.options.projectContext.project.get("id"),
                    quickView: true
                };

            if (target.hasClass("edit-experience")) {
                eventProperties.spectator = false;
                eventProperties.triggerOrigin = "EditExperienceButton";
                EditorEvents.trigger(EditorEvents.constants.OPEN_EXPERIENCE_EDITOR, eventProperties);
            }

            if (target.hasClass("view-experience")) {
                eventProperties.spectator = true;
                eventProperties.triggerOrigin = "ViewExperienceButton";
                EditorEvents.trigger(EditorEvents.constants.OPEN_EXPERIENCE_EDITOR, eventProperties);
            }

            this.trigger("closeQuickView", true);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experienceOverview/QuickViewExperienceDetailsView.js